
import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * A route that requires both authentication and a valid subscription.
 * Used for premium features that should only be accessible to paid users.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [hasValidSubscription, setHasValidSubscription] = useState(false);
  const [smsOptInStatus, setSmsOptInStatus] = useState<string | null>(null);
  
  // Check if a subscription is valid
  const checkSubscriptionStatus = async () => {
    try {
      if (!user) return false;
      
      setCheckingSubscription(true);
      console.log("Checking subscription status for user:", user.id);
      
      // Use the subscription status API
      const { data, error } = await supabase.functions.invoke('get-subscription-status');
      
      if (error) {
        console.error("Error checking subscription:", error);
        throw error;
      }
      
      // Log full subscription response data for debugging
      console.log("Full subscription API response:", JSON.stringify(data, null, 2));
      
      // Update state based on the API response - Use case insensitive comparison
      const isPro = data.tier && data.tier.toLowerCase() === 'pro';
      console.log(`User subscription tier: "${data.tier}", normalized: "${data.tier?.toLowerCase()}", isPro:`, isPro);
      console.log(`Has valid subscription:`, data.hasValidSubscription);
      console.log(`Expires at:`, data.expiresAt);
      
      setHasValidSubscription(data.hasValidSubscription && isPro);
      setSmsOptInStatus(data.smsOptInStatus || null);
      return data.hasValidSubscription && isPro;
      
    } catch (error) {
      console.error("Error in checkSubscriptionStatus:", error);
      setHasValidSubscription(false);
      return false;
    } finally {
      setCheckingSubscription(false);
    }
  };
  
  useEffect(() => {
    if (!loading && user) {
      checkSubscriptionStatus();
    }
  }, [user, loading]);
  
  // While checking authentication status, show a loading state
  if (loading || checkingSubscription) {
    return (
      <div className="flex min-h-screen items-center justify-center flex-col gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">
          {loading ? "Verifying your account..." : "Checking subscription status..."}
        </p>
      </div>
    );
  }
  
  // If not authenticated, redirect to login
  if (!user) {
    console.log("User not authenticated, redirecting to login");
    return <Navigate 
      to="/login" 
      replace 
      state={{ 
        returnTo: location.pathname + location.search
      }} 
    />;
  }
  
  // If authenticated but no valid subscription and not on pricing page, redirect to pricing
  if (!hasValidSubscription && location.pathname !== '/pricing') {
    console.log("User authenticated but no valid subscription, redirecting to pricing");
    toast.error("You need a Pro subscription to access this page", {
      position: "top-right"
    });
    return <Navigate 
      to="/pricing" 
      replace 
      state={{ 
        returnTo: location.pathname + location.search
      }} 
    />;
  }
  
  // If authenticated and has subscription but SMS opt-in pending, redirect to confirm-phone
  if (hasValidSubscription && smsOptInStatus === 'pending' && location.pathname !== '/confirm-phone') {
    console.log("User has subscription but SMS opt-in pending, redirecting to confirm-phone");
    return <Navigate to="/confirm-phone" replace />;
  }
  
  // If authenticated and has subscription, render the protected component
  return <>{children}</>;
};

export default ProtectedRoute;
